import { dayjs } from "@equidefi/i18n";
import { useCallback, useEffect, useState } from "react";
import { Dropzone } from "../Dropzone";

import { Box, Button, HStack, IconButton, Tooltip, VStack, useToast } from "@chakra-ui/react";

import { DROPZONE_ACCEPTED_TYPES, RESTRICTED_CONTENT_TYPES } from "@equidefi/constants/files";
import { useDisclosure, useIsMobile } from "@equidefi/hooks";
import { capitalizeEveryWord } from "@equidefi/shared";
import { BannerAlert, ConfirmModal, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import AWSClient from "../../clients/AWSClient";
import { useGetDocumentURLs, usePresignedUrl, useUpdateVault } from "../../hooks/useAccreditation";
import { useSavedProfilePresignedUrl, useUpdateSavedProfile } from "../../hooks/useSavedProfiles";
import { DEFAULT_MAX_FILES, DEFAULT_MAX_SIZE, handleRejectionFiles } from "./helpers";

export const FileListItemButton = ({
  icon,
  text,
  colorScheme = "blue",
  "aria-label": ariaLabel = text,
  variant = "ghost",
  isDisabled = false,
  isLoading = false,
  onClick = () => {},
  ...rest
}) => {
  const isMobile = useIsMobile();

  // Use IconButton if text is not provided or on mobile
  if (!text || isMobile) {
    return (
      <IconButton
        {...rest}
        icon={icon}
        colorScheme={colorScheme}
        isDisabled={isDisabled}
        isLoading={isLoading}
        variant={variant}
        aria-label={ariaLabel}
        onClick={onClick}
        size="xs"
        minW="auto"
        h="32px"
        w="32px"
      />
    );
  }

  return (
    <Button
      {...rest}
      rightIcon={icon}
      colorScheme={colorScheme}
      isDisabled={isDisabled}
      isLoading={isLoading}
      variant={variant}
      aria-label={ariaLabel}
      onClick={onClick}>
      {text}
    </Button>
  );
};

export const FileListItem = ({
  type,
  name,
  url,
  status,
  createDate,
  isDeletable,
  isEditable = false,
  canDownload = true,
  onEdit = () => {},
  onDelete = () => {},
  isDeleting = false,
  portalType = "investor",
}) => {
  const modal = useDisclosure();
  const isMobile = useIsMobile();

  // Non-Plaid files have no status, so fall back to url presence for them.
  const isFailed = status === "failed";
  const isDownloadable = (status ? status === "complete" : !!url) && typeof url === "string";
  const downloadTooltip = isFailed ? "Upload failed — please retry" : "Files are uploading, please wait";

  const formatUploadDate = (date) => {
    if (!date) return null;
    return `Uploaded on ${dayjs(date).format("MMM D, YYYY")}`;
  };

  return (
    <HStack justify="space-between" align="center" w="100%" spacing={5} pb={3}>
      <VStack align="start" spacing="2px" flex="1" minW={0} overflow="hidden">
        <Text isTruncated w="100%" fontSize="sm" lineHeight="21px" color="black" m={0}>
          {name}
        </Text>
        {type && (
          <Text fontSize="xs" lineHeight="18px" color="equidefi.darkGray" isTruncated w="100%" m={0}>
            {capitalizeEveryWord(type.split("_").join(" "))}
          </Text>
        )}
        {createDate && (
          <Text fontSize="xs" lineHeight="18px" color="equidefi.darkGray" m={0}>
            {formatUploadDate(createDate)}
          </Text>
        )}
      </VStack>
      <HStack spacing={1} flexShrink={0}>
        <HStack spacing={1}>
          <ConfirmModal
            title="Delete File"
            isOpen={modal.isOpen}
            onCancel={modal.onClose}
            onConfirm={async () => {
              await onDelete();
              modal.onClose();
            }}
            description={`Are you sure you want to delete "${name}"?`}
            confirmButtonProps={{ isLoading: isDeleting }}
            confirmButtonColor="red"
            confirmText="Delete"
            cancelText="Cancel"
          />

          {canDownload && (
            <Tooltip hasArrow isDisabled={isDownloadable} label={downloadTooltip}>
              <Box>
                <FileListItemButton
                  icon={<Icon.ExternalLink size="24px" />}
                  text={portalType === "investor" ? undefined : "Download"}
                  aria-label="Download file"
                  variant="ghost"
                  colorScheme={isFailed ? "red" : "blue"}
                  color={isFailed ? "equidefi.red" : "equidefi.blue"}
                  isDisabled={!isDownloadable}
                  onClick={() => window.open(url, "_blank")}
                />
              </Box>
            </Tooltip>
          )}

          {isEditable && portalType === "issuer" && (
            <FileListItemButton
              icon={<Icon.Edit3 size="24px" />}
              text={isMobile ? undefined : "Edit"}
              aria-label="Edit file"
              variant="ghost"
              colorScheme="blue"
              color="equidefi.blue"
              onClick={onEdit}
            />
          )}

          {isDeletable && (
            <FileListItemButton
              icon={<Icon.Trash2 size="24px" />}
              text={portalType === "investor" ? undefined : isMobile ? undefined : "Delete"}
              aria-label="Remove file"
              variant="ghost"
              colorScheme="red"
              color="equidefi.red"
              isLoading={isDeleting}
              onClick={modal.onToggle}
            />
          )}
        </HStack>
      </HStack>
    </HStack>
  );
};

export const UploadVaultUrlComponent = ({
  investmentId,
  field,
  fieldName,
  handleChange = () => {},
  onDelete = () => {},
  onUpload = () => {},
  noDocumentWarning,
  vault,
  savedProfileId = null,
  portalType = "investor",
  canEdit = true,
  canDownload = true,
}) => {
  const [file, setFile] = useState(null);
  const [spinner, setSpinner] = useState(false);
  const investmentPresignedUrl = usePresignedUrl(investmentId);
  const profilePresignedUrl = useSavedProfilePresignedUrl(savedProfileId ?? "");
  const getPresignedUrl = savedProfileId ? profilePresignedUrl : investmentPresignedUrl;
  const updateVault = useUpdateVault(investmentId);
  const updateSavedProfile = useUpdateSavedProfile(savedProfileId ?? "");
  const updater = savedProfileId ? updateSavedProfile : updateVault;
  const toast = useToast();
  const { data: downloadUrls, isLoading } = useGetDocumentURLs(vault?.id, {
    refetchOnMount: "always",
  });
  const hideDragDrop = !canEdit || vault?.status === "APPROVED" || file;

  useEffect(() => {
    if (downloadUrls?.[field]) {
      setFile({
        name: fieldName,
        field: field,
        url: downloadUrls[field],
      });

      handleChange({
        target: {
          name: field,
          value: downloadUrls[field],
        },
      });
    }
  }, [field, downloadUrls, isLoading]);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length === 0 && rejectedFiles.length === 0) {
        toast({
          id: "vault-upload-empty",
          status: "error",
          description: "We didn't receive your file. Please tap the upload area and try again.",
        });
        return;
      }

      const rejected = handleRejectionFiles(rejectedFiles, {
        onReject: (message) => {
          toast({
            status: "error",
            description: message,
          });
        },
      });

      if (rejected) return;

      setSpinner(true);
      for (const [, file] of acceptedFiles.entries()) {
        const filename = file.name.split(" ").join("_");
        const extension = filename.split(".").pop().toLowerCase();

        try {
          const response = await getPresignedUrl.mutateAsync({
            filename: field,
            fileExtension: extension,
          });
          getPresignedUrl.reset();

          const client = new AWSClient();
          await client.s3Upload(response.signedUrl, file, RESTRICTED_CONTENT_TYPES[extension]);
          await updater.mutateAsync(
            savedProfileId ? { [field]: response.url } : { [field]: response.url, user_id: vault.user_id },
          );
          updater.reset();

          onUpload(field);

          handleChange({
            target: {
              name: field,
              value: response.url,
            },
          });

          setFile({
            name: fieldName,
            field: response.savedField,
            url: response.previewSignedUrl,
          });

          toast({
            status: "success",
            description: "Document uploaded successfully!",
          });
        } catch (e) {
          console.error(e);
          toast({
            status: "error",
            description: "Sorry, the document upload failed.",
          });
        }
      }

      setSpinner(false);
    },
    [
      field,
      fieldName,
      getPresignedUrl,
      handleChange,
      onUpload,
      toast,
      updater.mutateAsync,
      updater.reset,
      savedProfileId,
      vault?.user_id,
    ],
  );

  const removeFile = useCallback(async () => {
    try {
      handleChange({
        target: {
          name: field,
          value: null,
        },
      });
      await updater.mutateAsync({
        [field]: null,
      });
      updater.reset();

      onDelete(field);
      setFile(null);

      toast({
        status: "success",
        description: "The file was removed!",
      });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: "Removing the file failed.",
      });
    }
  }, [handleChange, field]);

  return (
    <>
      <VStack spacing={1} align="stretch">
        {!file && canEdit && (
          <BannerAlert status="warning" description="You must upload a proof of address document" mb={2} />
        )}
        {!file && !canEdit && (
          <Text mb={0} color="gray.500" fontStyle="italic">
            No proof of address document has been uploaded.
          </Text>
        )}
        {file && (
          <FileListItem
            name={file.name}
            url={file.url}
            isDeletable={canEdit && vault?.status !== "APPROVED"}
            canDownload={canDownload}
            onDelete={removeFile}
            portalType={portalType}
          />
        )}

        {!hideDragDrop && (
          <Dropzone
            onDrop={onDrop}
            onFileDialogCancel={() =>
              toast({
                id: "vault-upload-empty",
                status: "error",
                description: "We didn't receive your file. Please tap the upload area and try again.",
              })
            }
            accept={DROPZONE_ACCEPTED_TYPES}
            maxFiles={DEFAULT_MAX_FILES}
            maxSize={DEFAULT_MAX_SIZE}
            isLoading={spinner}
            zoneText="Add New File"
            responsive={true}
          />
        )}
      </VStack>
    </>
  );
};
